<template>
  <div
    id="blum-header"
    class="sticky top-0 left-0 bl-header"
    :style="{ zIndex: 100 }"
  >
    <nav class="bl-header-nav">
      <router-link
        v-if="currentUser.id"
        :to="currentUserRole.includes('admin') ? '/team' : '/clients'"
        class="flex items-center text-2xl font-bold font-logo"
      >
        Blüm
      </router-link>
      <div v-else class="flex items-center text-2xl font-bold font-logo">
        Blüm
      </div>
      <div
        v-if="currentUser.id"
        class="flex items-center justify-end flex-grow w-full lg:justify-between"
      >
        <div class="hidden gap-6 lg:flex top-navigation">
          <div
            v-if="currentUserRole.includes('admin')"
            class="relative transition-all duration-300 cursor-pointer"
            :class="{ 'router-link-active': isCenterActive }"
            @click="centerMenuOpened = true"
          >
            <div class="flex items-center gap-2">
              <BlTextSubtitle2 text="Center" />
              <BlIcon
                icon="chevron-down"
                class="w-5 h-5 transition-all duration-300"
                :style="{ rotate: centerMenuOpened ? '180deg' : '' }"
              />
            </div>
            <div
              v-if="centerMenuOpened"
              class="absolute flex flex-col flex-shrink-0 w-full gap-1 px-2 py-2 mt-3 overflow-y-auto border rounded shadow-md min-w-48 bg-pure-white border-grey-200"
            >
              <div v-for="(item, idx) in centerMenu" :key="idx">
                <div v-if="item.divider" class="w-full h-px bg-slate-400"></div>
                <router-link
                  v-else
                  :to="item.target"
                  class="flex items-center flex-shrink-0 w-full gap-3 px-2 py-2 truncate transition-all duration-300 rounded cursor-pointer"
                  :class="[
                    item.isActive ? 'bg-slate-300' : 'hover:bg-hover-state'
                  ]"
                  :title="item.label"
                >
                  <BlTextSubtitle3
                    class="flex-shrink-0 truncate"
                    :text="item.label"
                  />
                </router-link>
              </div>
            </div>
          </div>
          <router-link
            v-if="!currentUserRole.includes('admin') && enableAppointment"
            to="/appointments"
            class="relative transition-all duration-300"
          >
            <BlTextSubtitle2 text="Appointments" />
          </router-link>
          <router-link
            v-if="currentUserRole.includes('admin') && enableSalesPipeline"
            to="/client-acquisition"
            class="relative transition-all duration-300"
          >
            <BlTextSubtitle2 text="Client Acquisition" />
          </router-link>
          <router-link
            to="/my-sessions"
            class="relative transition-all duration-300"
          >
            <BlTextSubtitle2 text="My Sessions" />
          </router-link>
          <router-link
            to="/clients"
            class="relative transition-all duration-300"
          >
            <BlTextSubtitle2 text="Clients" />
          </router-link>
          <div
            class="relative transition-all duration-300 cursor-pointer"
            :class="{ 'router-link-active': isRepositoryActive }"
            @click="repositoryMenuOpened = true"
          >
            <div class="flex items-center gap-2">
              <BlTextSubtitle2 text="Repository" />
              <BlIcon
                icon="chevron-down"
                class="w-5 h-5 transition-all duration-300"
                :style="{ rotate: repositoryMenuOpened ? '180deg' : '' }"
              />
            </div>
            <div
              v-if="repositoryMenuOpened"
              class="absolute flex flex-col flex-shrink-0 w-full gap-1 px-2 py-2 mt-3 overflow-y-auto border rounded shadow-md min-w-48 bg-pure-white border-grey-200"
            >
              <div v-for="(item, idx) in repositoryMenu" :key="idx">
                <div v-if="item.divider" class="w-full h-px bg-slate-400"></div>
                <router-link
                  v-else
                  :to="item.target"
                  class="flex items-center flex-shrink-0 w-full gap-3 px-2 py-2 truncate transition-all duration-300 rounded cursor-pointer"
                  :class="[
                    item.isActive ? 'bg-slate-300' : 'hover:bg-hover-state'
                  ]"
                  :title="item.label"
                >
                  <BlTextSubtitle3
                    class="flex-shrink-0 truncate"
                    :text="item.label"
                  />
                </router-link>
              </div>
            </div>
          </div>
        </div>
        <div class="relative">
          <div class="cursor-pointer" @click="profileMenuOpened = true">
            <Avatar
              :name="currentUser.name"
              class="transition-all duration-300 bg-red-salmon hover:bg-error-400"
            />
          </div>
          <div
            v-if="profileMenuOpened"
            class="absolute right-0 flex flex-col flex-shrink-0 w-full gap-1 px-2 py-2 mt-3 overflow-y-auto border rounded shadow-md min-w-64 bg-pure-white border-grey-200"
          >
            <div
              class="flex flex-col px-2 py-3 rounded"
              :class="{
                'bl-profile-admin': currentUserRole === 'admin',
                'bl-profile-staff': currentUserRole === 'staff',
                'bl-profile-super-admin': currentUserRole === 'super_admin'
              }"
            >
              <BlTextSubtitle3
                :text="currentUser.name"
                :class="{
                  'text-cornflower-700': currentUserRole === 'admin',
                  'text-lime-700': currentUserRole === 'staff',
                  'text-orange-700': currentUserRole === 'super_admin'
                }"
              />
              <BlTextBody3
                :text="currentUser.email"
                :class="{
                  'text-cornflower-700': currentUserRole === 'admin',
                  'text-lime-700': currentUserRole === 'staff',
                  'text-orange-700': currentUserRole === 'super_admin'
                }"
              />
              <div class="flex items-center gap-1 mt-1">
                <div
                  class="flex items-center h-6 px-2"
                  :class="{
                    'bg-cornflower-700': currentUserRole === 'admin',
                    'bg-lime-700': currentUserRole === 'staff',
                    'bg-orange-700': currentUserRole === 'super_admin'
                  }"
                >
                  <BlTextBody3
                    :text="userRoles[currentUserRole]"
                    class="text-pure-white"
                  />
                </div>
                <div
                  v-if="currentUser.is_owner"
                  class="flex items-center h-6 px-2 bg-dark-purple-100"
                >
                  <BlTextBody3 text="Owner" class="text-pure-white" />
                </div>
              </div>
            </div>
            <div v-for="(item, idx) in profileMenu" :key="idx">
              <div v-if="item.divider" class="w-full h-px bg-slate-400"></div>
              <router-link
                v-else
                :to="item.target"
                class="flex items-center flex-shrink-0 w-full gap-3 px-2 py-2 truncate transition-all duration-300 rounded cursor-pointer"
                :class="[
                  item.isActive ? 'bg-slate-300' : 'hover:bg-hover-state'
                ]"
                :title="item.label"
              >
                <BlTextBody2
                  class="flex-shrink-0 truncate"
                  :text="item.label"
                />
              </router-link>
            </div>
            <div class="flex flex-col gap-1">
              <div
                class="flex items-center flex-shrink-0 w-full gap-3 px-2 py-2 truncate transition-all duration-300 rounded cursor-pointer hover:bg-hover-state"
                @click="signOut"
                title="Sign Out"
              >
                <BlTextBody2
                  class="flex-shrink-0 truncate text-error-400"
                  text="Sign Out"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import Avatar from "./Avatar.vue";
import BlIcon from "./BlIcon.vue";
import BlInputButton from "./BlInputButton.vue";
import BlTextSubtitle2 from "./BlTextSubtitle2.vue";
import BlTextSubtitle3 from "./BlTextSubtitle3.vue";
import BlTextBody2 from "./BlTextBody2.vue";
import BlTextBody3 from "./BlTextBody3.vue";

export default {
  name: "Header",
  components: {
    Avatar,
    BlIcon,
    BlInputButton,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody2,
    BlTextBody3
  },
  data() {
    return {
      loadingSignOut: false,
      centerMenuOpened: false,
      repositoryMenuOpened: false,
      profileMenuOpened: false,
      //
      userRoles: {
        admin: "Admin",
        staff: "Therapist",
        super_admin: "Super admin"
      }
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter"]),
    dataId() {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      let combination = "";
      for (let i = 0; i < 6; i++) {
        combination += chars[Math.round(Math.random() * 35)];
      }
      return `bl-header-menu-${combination}`;
    },
    currentUserRole() {
      return this.currentUser.role || "";
    },
    enableAppointment() {
      return this.currentCenter.enable_appointment;
    },
    enableSalesPipeline() {
      return this.currentCenter.enable_sales_pipeline;
    },
    enableAnalytics() {
      return this.currentCenter.enable_analytics;
    },
    //
    isCenterActive() {
      if (this.$route.name.includes("Finance")) return true;
      if (this.$route.name.includes("Availabilities")) return true;
      if (this.$route.name.includes("Appointment")) return true;
      if (this.$route.name.includes("Analytics")) return true;
      if (this.$route.name.includes("Team")) return true;
      if (this.$route.name.includes("Center")) return true;
      return false;
    },
    centerMenu() {
      const appointment = [
        {
          target: "/finance",
          isActive: this.$route.name.includes("Finance"),
          label: "Finance"
        },
        {
          target: "/availabilities",
          isActive: this.$route.name.includes("Availabilities"),
          label: "Availabilities"
        },
        {
          target: "/appointments",
          isActive: this.$route.name.includes("Appointment"),
          label: "Appointments"
        }
      ];
      const analytics = [
        {
          target: "/analytics",
          isActive: this.$route.name.includes("Analytics"),
          label: "Analytics"
        }
      ];
      return [
        ...(this.enableAppointment ? appointment : []),
        ...(this.enableAnalytics ? analytics : []),
        ...(this.enableAppointment || this.enableAnalytics
          ? [{ divider: true }]
          : []),
        {
          target: "/team",
          isActive: this.$route.name.includes("Team"),
          label: "Team"
        },
        {
          target: "/center",
          isActive: this.$route.name.includes("Center"),
          label: "Center Settings"
        }
      ];
    },
    //
    isRepositoryActive() {
      if (this.$route.name.includes("Repository")) return true;
      if (this.$route.name === "Curriculum") return true;
      return false;
    },
    repositoryMenu() {
      const tab = this.$route.name === "RepositoryTab";
      return [
        {
          target: "/repository",
          isActive: this.$route.name === "RepositoryTags",
          label: "Tags"
        },
        { divider: true },
        {
          target: "/repository/curriculum",
          isActive:
            (tab && this.$route.params.tab === "curriculum") ||
            this.$route.name === "Curriculum",
          label: "Curricula"
        },
        {
          target: "/repository/databank",
          isActive: tab && this.$route.params.tab === "databank",
          label: "Databank"
        },
        { divider: true },
        {
          target: "/repository/prompting",
          isActive: tab && this.$route.params.tab === "prompting",
          label: "Prompting"
        },
        ...(this.currentCenter.enable_sbt ? [
          {
            target: "/repository/skill-based-treatment",
            isActive: tab && this.$route.params.tab === "skill-based-treatment",
            label: "Skill-Based Treatment"
          }
        ] : [])
      ];
    },
    profileMenu() {
      const isOwner = [
        {
          target: "/billing",
          isActive: this.$route.name === "Billing",
          label: "Billing"
        }
      ];
      const superAdmin = [
        { divider: true },
        {
          target: "/super-admin",
          isActive: this.$route.name.includes("SuperAdmin"),
          label: "Center Management"
        }
      ];
      return [
        {
          target: "/profile",
          isActive: this.$route.name === "Profile",
          label: "Profile"
        },
        ...(this.currentUser.is_owner || this.currentUserRole === "super_admin"
          ? isOwner
          : []),
        ...(this.currentUserRole === "super_admin" ? superAdmin : []),
        { divider: true }
      ];
    }
  },
  watch: {
    centerMenuOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.centerCloseHandler);
        }, 10);
      }
    },
    repositoryMenuOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.repositoryCloseHandler);
        }, 10);
      }
    },
    profileMenuOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.profileCloseHandler);
        }, 10);
      }
    }
  },
  methods: {
    ...mapActions(["showToast"]),
    centerCloseHandler(e) {
      window.removeEventListener("click", this.centerCloseHandler);
      this.centerMenuOpened = false;
    },
    repositoryCloseHandler(e) {
      window.removeEventListener("click", this.repositoryCloseHandler);
      this.repositoryMenuOpened = false;
    },
    profileCloseHandler(e) {
      window.removeEventListener("click", this.profileCloseHandler);
      this.profileMenuOpened = false;
    },
    onMove(nav) {
      this.$router.push(nav.target);
    },
    async signOut() {
      this.loadingSignOut = true;
      this.$http.secured
        .delete("/signin")
        .then(() => {
          localStorage.removeItem("csrf");
          localStorage.removeItem("access");
          localStorage.removeItem("signedIn");
          localStorage.removeItem("currentUserId");
          localStorage.removeItem("currentUserName");
          localStorage.removeItem("currentUserEmail");
          localStorage.removeItem("currentUserRole");
          const title = "You have successfully signed out";
          this.showToast({ title, message: "", type: "success" });
          setTimeout(() => {
            this.$router.replace("/");
          }, 300);
        })
        .catch(error => {
          this.loadingSignOut = false;
          const message =
            (error.response &&
              error.response.data &&
              error.response.data.error) ||
            "Unable to sign out.";
          this.showToast({ message, type: "error" });
        });
    }
  }
};
</script>

<style scoped lang="scss">
.bl-header {
  width: 100%;
  height: 4.5rem;
  box-sizing: border-box;
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  // z-index: 50;
  nav.bl-header-nav {
    position: relative;
    width: 100%;
    padding: 1.25rem 1.5rem;
    box-sizing: border-box;
    background: var(--accent-color);
    display: flex;
    align-items: center;
    gap: 2rem;
  }
}
.top-navigation .router-link-active::before {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: -0.375rem;
  height: 4px;
  background: #6750a4;
  border: 1px solid #6750a4;
  border-radius: 5px;
}
@media screen and (max-width: 1023px) {
  .top-navigation .router-link-active::before {
    width: 3rem;
  }
  .bl-header {
    nav.bl-header-nav {
      padding: 1.25rem 1rem;
    }
  }
}

.bl-profile-admin {
  position: relative;
  overflow: hidden;
  background: #f5fbff;
  border: 1px solid #eaecf0;
}
.bl-profile-admin::after {
  content: "";
  position: absolute;
  width: 74px;
  height: 74px;
  border-radius: 74px;
  top: 38px;
  right: -38px;
  gap: 0px;
  opacity: 0px;
  background: #7f56d9;
  filter: blur(38px);
}

.bl-profile-staff {
  position: relative;
  overflow: hidden;
  background: #f4fbcb;
  border: 1px solid #eaecf0;
}
.bl-profile-staff::after {
  content: "";
  position: absolute;
  width: 74px;
  height: 74px;
  border-radius: 74px;
  top: 38px;
  right: -38px;
  gap: 0px;
  opacity: 0px;
  background: #ff3838;
  filter: blur(38px);
}

.bl-profile-owner {
  position: relative;
  overflow: hidden;
  background: #fafaff;
  border: 1px solid #eaecf0;
}
.bl-profile-owner::after {
  content: "";
  position: absolute;
  width: 74px;
  height: 74px;
  border-radius: 74px;
  top: 38px;
  right: -38px;
  gap: 0px;
  opacity: 0px;
  background: #ff4447;
  filter: blur(38px);
}

.bl-profile-super-admin {
  position: relative;
  overflow: hidden;
  background: #fff6ed;
  border: 1px solid #eaecf0;
}
.bl-profile-super-admin::after {
  content: "";
  position: absolute;
  width: 74px;
  height: 74px;
  border-radius: 74px;
  top: 38px;
  right: -38px;
  gap: 0px;
  opacity: 0px;
  background: #b9d843;
  filter: blur(38px);
}
</style>



// WEBPACK FOOTER //
// src/components/Header.vue